/**
 * Module Deep Link Composable
 *
 * Handles deep link state for esmodule usage directly without additional store.
 * Manages betslip opening based on props.
 */

import { computed, ref, watchEffect } from 'vue';

export interface ModuleDeepLinkProps {
  /** View type (e.g., 'betslip') */
  view?: string;
  /** Contract ID */
  conid?: string | number;
  /** Product Contract ID */
  productConid?: string | number;
  /** Product Contract ID (kebab-case from moduleProps) */
  'product-conid'?: string | number;
  query?: Record<string, string | number | undefined>;
}

// Global state for deep link
const deepLinkState = ref<{
  view?: string;
  conid?: string | number;
  productConid?: string | number;
  showShareDialog?: string | number;
}>({});

/**
 * Composable for handling module deep link props
 * @param props - The props passed to the module component
 * @returns Deep link state and utilities
 */
export function useModuleDeepLink(props: ModuleDeepLinkProps) {
  // Watch props and update state
  watchEffect(() => {
    if (props.query) {
      deepLinkState.value = {
        view: props.query.view as string,
        conid: props.query.conid,
        productConid: props.query.productConid,
        showShareDialog: props.query.show_share_dialog ?? props.query.showShareDialog,
      };
    } else if (props.view || props.conid || props.productConid || props['product-conid']) {
      // Use individual props (handle both camelCase and kebab-case)
      deepLinkState.value = {
        view: props.view,
        conid: props.conid,
        productConid: props.productConid || props['product-conid'],
      };
    } else {
      // Clear state if no props provided
      deepLinkState.value = {};
    }
  });

  // Computed: should open betslip
  const shouldOpenBetSlip = computed(() => {
    return (
      deepLinkState.value.view === 'sharecontracts' && !!(deepLinkState.value.conid || deepLinkState.value.productConid)
    );
  });

  // Computed: betslip config
  const betSlipConfig = computed(() => {
    if (!shouldOpenBetSlip.value) {
      return null;
    }

    return {
      conid: deepLinkState.value.conid ? Number(deepLinkState.value.conid) : undefined,
      productConid: deepLinkState.value.productConid ? Number(deepLinkState.value.productConid) : undefined,
      showShareDialog: String(deepLinkState.value.showShareDialog) === '1',
    };
  });

  // Computed: should open share dialog
  const shouldOpenShareDialog = computed(() => {
    return (
      deepLinkState.value.view === 'sharedialog' && !!(deepLinkState.value.conid || deepLinkState.value.productConid)
    );
  });

  // Computed: share dialog config
  const shareDialogConfig = computed(() => {
    if (!shouldOpenShareDialog.value) {
      return null;
    }

    return {
      conid: deepLinkState.value.conid ? Number(deepLinkState.value.conid) : undefined,
      productConid: deepLinkState.value.productConid ? Number(deepLinkState.value.productConid) : undefined,
    };
  });

  return {
    // State
    deepLinkState,

    // Computed
    shouldOpenBetSlip,
    betSlipConfig,
    shouldOpenShareDialog,
    shareDialogConfig,
  };
}
